import { useEffect, useState } from 'react';
import { Box, Grid, Link, Typography } from '@mui/material';
import { ExpansionPanel, InputField, CurrencySelect } from '@/components/common';
import { useCompanyAnnualStore } from '@/store/audit/companyAnnualStore';
import type { CompanyAnnual } from '@/services/accountService/companyAnnualService';

type Props = {
  annualId: number;
  onChange?: (patch: Partial<CompanyAnnual>) => void;
  disableAccordion?: boolean;
  initialData?: {
    presentationCurrency?: string | null;
    functionalCurrency?: string | null;
  } | null;
};

export default function EpCurrencyExchange({ annualId, onChange, disableAccordion = false, initialData }: Props) {
  const store = useCompanyAnnualStore();
  const [local, setLocal] = useState<{ presentationCurrency: string | null; exchangeRate?: number; functionalCurrency: string | null }>({
    presentationCurrency: 'HKD',
    exchangeRate: undefined,
    functionalCurrency: 'HKD',
  });

  useEffect(() => {
    if (initialData) {
      setLocal(prev => ({
        ...prev,
        presentationCurrency: initialData.presentationCurrency || prev.presentationCurrency || 'HKD',
        functionalCurrency: initialData.functionalCurrency || prev.functionalCurrency || 'HKD',
      }));
      return;
    }

    let mounted = true;
    (async () => {
      const v = await store.getCurrencyAndExchangeRateInfo(annualId);
      if (!mounted) return;
      setLocal({
        presentationCurrency: v.presentationCurrency || 'HKD',
        exchangeRate: v.exchangeRate,
        functionalCurrency: v.functionalCurrency || 'HKD',
      });
    })();
    return () => { mounted = false; };
  }, [annualId, store, initialData]);

  const emit = (patch: Partial<typeof local>) => {
    const next = { ...local, ...patch };
    setLocal(next);
    onChange?.(patch as Partial<CompanyAnnual>);
  };

  return (
    <ExpansionPanel 
      title="Currency & Exchange Rate (貨幣與匯率)"
      expanded={disableAccordion ? true : undefined}
      onChange={disableAccordion ? () => {} : undefined}
    >
      <Grid container spacing={0.5}>
        <Grid size={6 as any}>
          <CurrencySelect variant="outlined" label="Presentation Currency (報告貨幣)" value={local.presentationCurrency || 'HKD'} onChange={(code: string) => emit({ presentationCurrency: code })} />
        </Grid>
        {local.presentationCurrency === 'HKD' ? null : (
          <Grid size={6 as any}>
            <InputField
              fullWidth
              type="money"
              variant="outlined"
              label="Exchange Rate (匯率)"
              value={local.exchangeRate ?? ''}
              popoverContent={
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
                  <Typography variant="body2">
                    如果Pecentation Currency 不是 HKD, 便要Exchange 成 HDK,
                  </Typography>
                  <Typography variant="body2">
                    用以計 Tax Computation 及 Tax Reture
                  </Typography>
                  <Link
                    href="https://www.ird.gov.hk/eng/tax/bus_aer.htm"
                    target="_blank"
                    rel="noopener noreferrer"
                    variant="body2"
                  >
                    Pleace check IRD: Average Exchange Rate
                  </Link>
                </Box>
              }
              onChange={(e) => emit({ exchangeRate: e.target.value as any })}
              onBlur={(e) => {
                const raw = String(e.target.value ?? '').replace(/,/g, '').trim();
                const n = raw === '' || isNaN(Number(raw)) ? 0 : Number(raw);
                emit({ exchangeRate: n as any });
              }}
            />
          </Grid>
        )}
        <Grid size={6 as any}>
          <CurrencySelect variant="outlined" label="Functional currency (公司的功能性貨幣)" value={local.functionalCurrency || 'HKD'} onChange={(code: string) => emit({ functionalCurrency: code })} />
        </Grid>
      </Grid>
    </ExpansionPanel>
  );
}
